import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiEndpoint, ApiParameter, TryItResponse } from '../api-spec.model';
import { JsonSyntaxHighlightPipe } from '../pipes/json-syntax-highlight.pipe';

@Component({
  selector: 'app-try-it-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, JsonSyntaxHighlightPipe],
  template: `
    <div class="h-full flex flex-col bg-white">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h3 class="text-sm font-semibold text-gray-900">Try It</h3>
        <button type="button" (click)="closePanel.emit()"
          class="xl:hidden p-1 text-gray-400 hover:text-gray-600 rounded">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-4 space-y-4">
        <!-- ═══ Auth Section ═══ -->
        <div class="rounded-lg border border-gray-200 overflow-hidden">
          <button type="button" (click)="authExpanded = !authExpanded"
            class="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
            <div class="flex items-center gap-2">
              <svg class="w-3.5 h-3.5 text-gray-400 transition-transform duration-200" [class.rotate-90]="authExpanded"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-xs font-semibold text-gray-700">Authentication</span>
            </div>
            <div *ngIf="token" class="flex items-center gap-1.5">
              <div class="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
              <span class="text-[10px] text-emerald-600 font-medium">{{ formatTime(tokenExpiresIn) }}</span>
            </div>
          </button>

          <div *ngIf="authExpanded" class="p-3 space-y-3 border-t border-gray-100">
            <div>
              <label class="block text-[11px] font-medium text-gray-600 mb-1">app_key</label>
              <input type="text" [(ngModel)]="appKey" placeholder="Your application key"
                class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 outline-none" />
            </div>
            <div>
              <label class="block text-[11px] font-medium text-gray-600 mb-1">app_secret</label>
              <div class="relative">
                <input [type]="showSecret ? 'text' : 'password'" [(ngModel)]="appSecret" placeholder="Your application secret"
                  class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 pr-8 outline-none" />
                <button type="button" (click)="showSecret = !showSecret"
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path *ngIf="!showSecret" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path *ngIf="!showSecret" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    <path *ngIf="showSecret" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" />
                  </svg>
                </button>
              </div>
            </div>

            <button type="button" (click)="generateToken()" [disabled]="!appKey || !appSecret || authLoading"
              class="w-full px-3 py-2 text-xs font-medium text-white rounded-lg flex items-center justify-center gap-2 transition-colors bg-westetics-purple-800 hover:bg-westetics-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed">
              <svg *ngIf="authLoading" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {{ authLoading ? 'Generating...' : 'Generate Token' }}
            </button>

            <!-- Token success -->
            <div *ngIf="token" class="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 space-y-2">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1.5">
                  <div class="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span class="text-[11px] font-medium text-emerald-700">Authenticated</span>
                </div>
                <button type="button" (click)="copyToken()"
                  class="text-[10px] text-emerald-600 hover:text-emerald-800 transition-colors">
                  {{ authCopied ? '✓' : 'Copy' }}
                </button>
              </div>
              <code class="block text-[10px] bg-white px-2 py-1 rounded border border-emerald-200 font-mono text-gray-600 truncate">
                {{ token.length > 40 ? token.substring(0, 40) + '...' : token }}
              </code>
              <div class="flex flex-wrap gap-1">
                <span *ngFor="let scope of tokenScopes"
                  class="px-1.5 py-0.5 text-[9px] bg-white text-emerald-700 border border-emerald-200 rounded">
                  {{ scope }}
                </span>
              </div>
            </div>

            <!-- Auth error -->
            <div *ngIf="authError" class="bg-red-50 border border-red-200 rounded-lg p-2.5">
              <p class="text-[11px] text-red-700">{{ authError }}</p>
            </div>

            <!-- Auth response -->
            <div *ngIf="authResponseBody" class="space-y-1.5">
              <div class="flex items-center gap-2">
                <span class="px-1.5 py-0.5 text-[10px] font-bold rounded"
                  [ngClass]="{
                    'bg-emerald-100 text-emerald-700': authResponseStatus >= 200 && authResponseStatus < 300,
                    'bg-orange-100 text-orange-700': authResponseStatus >= 400 && authResponseStatus < 500,
                    'bg-red-100 text-red-700': authResponseStatus >= 500 || authResponseStatus === 0
                  }">{{ authResponseStatus }}</span>
                <span class="text-[10px] text-gray-500">{{ authResponseTime }}ms</span>
              </div>
              <pre class="code-block"><code [innerHTML]="authResponseBody | jsonHighlight"></code></pre>
            </div>
          </div>
        </div>

        <!-- ═══ Endpoint Section ═══ -->
        <div *ngIf="activeEndpoint; else noEndpoint" class="space-y-3">
          <!-- Active endpoint header -->
          <div class="flex items-center gap-2 pb-2 border-b border-gray-100">
            <span class="px-2 py-0.5 text-[10px] font-bold rounded uppercase" [ngClass]="methodColor">
              {{ activeEndpoint.method }}
            </span>
            <code class="text-[11px] font-mono text-gray-700 break-all leading-tight">{{ activeEndpoint.path }}</code>
          </div>

          <!-- Token warning -->
          <div *ngIf="activeEndpoint.scope && !token"
            class="flex items-start gap-2 px-2.5 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg text-[11px]">
            <svg class="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Generate a token first.</span>
          </div>

          <!-- Path parameters -->
          <div *ngFor="let param of getPathParams()" class="space-y-1">
            <label class="text-[11px] font-medium text-gray-600">
              {{ param.name }} <span class="text-red-500">*</span>
            </label>
            <input type="text" [(ngModel)]="paramValues[param.name]" [placeholder]="param.description"
              class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 outline-none" />
          </div>

          <!-- Query parameters -->
          <div *ngIf="getQueryParams().length" class="space-y-2">
            <span class="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Query Parameters</span>
            <div class="space-y-2">
              <div *ngFor="let param of getQueryParams()">
                <label class="block text-[11px] font-medium text-gray-600 mb-1">
                  {{ param.name }}
                  <span *ngIf="param.required" class="text-red-500">*</span>
                </label>
                <select *ngIf="param.enum; else textInput" [(ngModel)]="paramValues[param.name]"
                  class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white">
                  <option *ngFor="let opt of param.enum" [value]="opt">{{ opt }}</option>
                </select>
                <ng-template #textInput>
                  <input type="text" [(ngModel)]="paramValues[param.name]"
                    [placeholder]="param.defaultValue || param.description"
                    class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 outline-none" />
                </ng-template>
              </div>
            </div>
          </div>

          <!-- Request body -->
          <div *ngIf="activeEndpoint.requestBody">
            <label class="block text-[11px] font-medium text-gray-600 mb-1">Request Body (JSON)</label>
            <textarea [(ngModel)]="requestBodyValue" rows="6" spellcheck="false"
              class="w-full px-2.5 py-2 text-[11px] font-mono border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 outline-none leading-relaxed"></textarea>
          </div>

          <!-- Send button -->
          <button type="button" (click)="sendRequest()" [disabled]="!canSend || loading"
            class="w-full px-4 py-2 text-xs font-medium text-white rounded-lg flex items-center justify-center gap-2 transition-colors bg-westetics-purple-800 hover:bg-westetics-purple-600 disabled:opacity-50 disabled:cursor-not-allowed">
            <svg *ngIf="loading" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ loading ? 'Sending...' : 'Send Request' }}
          </button>

          <!-- Response -->
          <div *ngIf="response" class="space-y-2 pt-1">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="px-1.5 py-0.5 text-[10px] font-bold rounded" [ngClass]="getStatusClass(response.status)">
                  {{ response.status }} {{ response.statusText }}
                </span>
                <span class="text-[10px] text-gray-500">{{ response.time }}ms</span>
              </div>
              <button type="button" (click)="copyResponse()"
                class="text-[10px] text-gray-500 hover:text-gray-700 transition-colors">
                {{ copiedResponse ? '✓ Copied' : 'Copy' }}
              </button>
            </div>

            <!-- Rate limit headers -->
            <div *ngIf="response.headers['x-ratelimit-limit']"
              class="flex flex-wrap gap-3 text-[10px] text-gray-500 bg-gray-100 rounded px-2.5 py-1.5">
              <span>Limit: <strong>{{ response.headers['x-ratelimit-limit'] }}</strong></span>
              <span>Remaining: <strong>{{ response.headers['x-ratelimit-remaining'] }}</strong></span>
              <span>Reset: <strong>{{ response.headers['x-ratelimit-reset'] }}s</strong></span>
            </div>

            <pre class="code-block"><code [innerHTML]="response.body | jsonHighlight"></code></pre>
          </div>
        </div>

        <!-- No endpoint placeholder -->
        <ng-template #noEndpoint>
          <div class="text-center py-8 px-4">
            <svg class="w-10 h-10 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-xs text-gray-400 leading-relaxed">
              Click <strong class="text-gray-500">Try It</strong> on any endpoint<br />to test it here.
            </p>
          </div>
        </ng-template>
      </div>
    </div>
  `,
  styles: [`:host
  display: block
  height: 100%
.code-block
  background: #111827
  color: #e5e7eb
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Courier New', monospace
  font-size: 0.6875rem
  line-height: 1.5
  border-radius: 0.375rem
  padding: 0.625rem
  overflow-x: auto
  margin: 0
:host ::ng-deep .jh-key
  color: #c4b5fd
:host ::ng-deep .jh-string
  color: #6ee7b7
:host ::ng-deep .jh-number
  color: #93c5fd
:host ::ng-deep .jh-boolean
  color: #fcd34d
:host ::ng-deep .jh-null
  color: #9ca3af
`]
})
export class TryItPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() apiBaseUrl = '';
  @Input() activeEndpoint: ApiEndpoint | null = null;
  @Output() tokenChange = new EventEmitter<string>();
  @Output() closePanel = new EventEmitter<void>();

  private static readonly STORAGE_KEY = 'wst_api_docs_tryit';

  // Auth state
  appKey = '';
  appSecret = '';
  showSecret = false;
  authLoading = false;
  authExpanded = true;
  token = '';
  tokenScopes: string[] = [];
  tokenExpiresAt = 0;
  tokenExpiresIn = 0;
  authError = '';
  authResponseBody = '';
  authResponseStatus = 0;
  authResponseTime = 0;
  authCopied = false;

  // Endpoint state
  loading = false;
  paramValues: Record<string, string> = {};
  requestBodyValue = '';
  response: TryItResponse | null = null;
  copiedResponse = false;

  private expirationInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.loadState();
  }

  get methodColor(): string {
    if (!this.activeEndpoint) return '';
    switch (this.activeEndpoint.method) {
      case 'GET': return 'bg-emerald-100 text-emerald-700';
      case 'POST': return 'bg-blue-100 text-blue-700';
      case 'PUT': return 'bg-orange-100 text-orange-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  get canSend(): boolean {
    if (!this.activeEndpoint) return false;
    if (this.activeEndpoint.scope && !this.token) return false;
    const required = this.getPathParams().filter(p => p.required);
    return required.every(p => !!this.paramValues[p.name]?.trim());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeEndpoint'] && this.activeEndpoint) {
      this.paramValues = {};
      this.response = null;
      this.copiedResponse = false;
      this.loading = false;
      this.requestBodyValue = this.activeEndpoint.requestBody?.example || '';
      this.activeEndpoint.parameters?.forEach(p => {
        if (p.defaultValue) {
          this.paramValues[p.name] = p.defaultValue;
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.expirationInterval) {
      clearInterval(this.expirationInterval);
    }
  }

  // ─── Auth methods ───

  async generateToken(): Promise<void> {
    this.authLoading = true;
    this.authError = '';
    this.authResponseBody = '';

    const startTime = performance.now();

    try {
      const res = await fetch(`${this.apiBaseUrl}/partner/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_key: this.appKey,
          app_secret: this.appSecret,
          grant_type: 'client_credentials'
        })
      });

      this.authResponseTime = Math.round(performance.now() - startTime);
      this.authResponseStatus = res.status;

      const data = await res.json();
      this.authResponseBody = JSON.stringify(data, null, 2);

      if (res.ok) {
        this.token = data.access_token;
        this.tokenScopes = data.scopes ? data.scopes.split(',') : [];
        this.tokenExpiresAt = Date.now() + (data.expires_in * 1000);
        this.startExpirationTimer();
        this.tokenChange.emit(this.token);
        this.authExpanded = false;
      } else {
        this.authError = data.message || `Authentication failed (${res.status})`;
      }
    } catch (e: any) {
      this.authError = `Network error: ${e.message}`;
      this.authResponseStatus = 0;
      this.authResponseTime = Math.round(performance.now() - startTime);
    } finally {
      this.authLoading = false;
      this.saveState();
    }
  }

  copyToken(): void {
    navigator.clipboard.writeText(this.token);
    this.authCopied = true;
    setTimeout(() => (this.authCopied = false), 2000);
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // ─── Endpoint methods ───

  getPathParams(): ApiParameter[] {
    return this.activeEndpoint?.parameters?.filter(p => p.location === 'path') || [];
  }

  getQueryParams(): ApiParameter[] {
    return this.activeEndpoint?.parameters?.filter(p => p.location === 'query') || [];
  }

  async sendRequest(): Promise<void> {
    if (!this.activeEndpoint) return;
    this.loading = true;
    this.response = null;

    const url = this.buildUrl();
    const startTime = performance.now();

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

      const options: RequestInit = { method: this.activeEndpoint.method, headers };
      if (this.activeEndpoint.method === 'POST' && this.requestBodyValue) {
        options.body = this.requestBodyValue;
      }

      const res = await fetch(url, options);
      const endTime = performance.now();

      const responseHeaders: Record<string, string> = {};
      res.headers.forEach((value, key) => { responseHeaders[key] = value; });

      let body: string;
      const text = await res.text();
      try { body = JSON.stringify(JSON.parse(text), null, 2); } catch { body = text; }

      this.response = {
        status: res.status,
        statusText: res.statusText,
        time: Math.round(endTime - startTime),
        headers: responseHeaders,
        body
      };
    } catch (error: any) {
      this.response = {
        status: 0,
        statusText: 'Network Error',
        time: Math.round(performance.now() - startTime),
        headers: {},
        body: JSON.stringify({ error: error.message }, null, 2)
      };
    } finally {
      this.loading = false;
    }
  }

  copyResponse(): void {
    if (this.response) {
      navigator.clipboard.writeText(this.response.body);
      this.copiedResponse = true;
      setTimeout(() => (this.copiedResponse = false), 2000);
    }
  }

  getStatusClass(status: number): string {
    if (status >= 200 && status < 300) return 'bg-emerald-100 text-emerald-700';
    if (status >= 400 && status < 500) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  }

  // ─── Private ───

  private buildUrl(): string {
    if (!this.activeEndpoint) return '';
    let path = this.activeEndpoint.path;

    this.getPathParams().forEach(p => {
      path = path.replace(`{${p.name}}`, encodeURIComponent(this.paramValues[p.name] || ''));
    });

    const queryEntries = this.getQueryParams()
      .filter(p => this.paramValues[p.name]?.trim())
      .map(p => `${encodeURIComponent(p.name)}=${encodeURIComponent(this.paramValues[p.name])}`);

    if (queryEntries.length) path += '?' + queryEntries.join('&');

    return this.apiBaseUrl + path;
  }

  private startExpirationTimer(): void {
    if (this.expirationInterval) clearInterval(this.expirationInterval);
    this.updateExpiration();
    this.expirationInterval = setInterval(() => this.updateExpiration(), 1000);
  }

  private updateExpiration(): void {
    const remaining = Math.max(0, this.tokenExpiresAt - Date.now());
    this.tokenExpiresIn = Math.floor(remaining / 1000);
    if (this.tokenExpiresIn <= 0) {
      if (this.expirationInterval) clearInterval(this.expirationInterval);
      this.token = '';
      this.tokenChange.emit('');
      this.saveState();
    }
  }

  private saveState(): void {
    try {
      const state = {
        appKey: this.appKey,
        appSecret: this.appSecret,
        token: this.token,
        tokenScopes: this.tokenScopes,
        tokenExpiresAt: this.tokenExpiresAt,
        authResponseBody: this.authResponseBody,
        authResponseStatus: this.authResponseStatus,
        authResponseTime: this.authResponseTime
      };
      localStorage.setItem(TryItPanelComponent.STORAGE_KEY, JSON.stringify(state));
    } catch { /* quota exceeded or private mode */ }
  }

  private loadState(): void {
    try {
      const raw = localStorage.getItem(TryItPanelComponent.STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);

      this.appKey = state.appKey || '';
      this.appSecret = state.appSecret || '';

      // Only restore token if it hasn't expired
      if (state.tokenExpiresAt && state.tokenExpiresAt > Date.now()) {
        this.token = state.token || '';
        this.tokenScopes = state.tokenScopes || [];
        this.tokenExpiresAt = state.tokenExpiresAt;
        this.authResponseBody = state.authResponseBody || '';
        this.authResponseStatus = state.authResponseStatus || 0;
        this.authResponseTime = state.authResponseTime || 0;
        this.startExpirationTimer();
        this.tokenChange.emit(this.token);
        this.authExpanded = false;
      }
    } catch { /* corrupt data */ }
  }
}
